import Title_ru_RU from '@/core/misc/decorators/Title_ru_RU';
import serializeSimplified from '@/core/misc/decorators/SerializeSimplified';

import TYPES from '@/core/services/Changelogs/types';

import TransportRoute from '@/domains/TransportRoutes/TransportRoute';
import type Offer from '@/domains/Offers/Offer';
import type OfferCharacteristic from '@/domains/Offers/OfferCharacteristic';
import type Demand from '@/domains/Demand/Demand';
import type Tender from '@/domains/Tenders/Tender';
import type Product from '@/domains/Products/Product';

import Core from '@/core/Core';
import Mismatch, {type MismatchItem} from '@/core/services/Mismatch';
import DeliveryType from '@/domains/DeliveryTypes/DeliveryType';
import Price from '@/core/types/Price';
import DateRange from '@/core/types/DateRange';
import DateSimple from '@/core/types/DateSimple';
import IntRange from '@/core/types/IntRange';
import BaseEntity from '@/core/misc/BaseEntity';
import PaymentTerm from '@/domains/PaymentTerms/PaymentTerm';
import PaymentTermsService from '@/domains/PaymentTerms/PaymentTermsService';
import Manufacturer from '@/domains/Manufacturers/Manufacturer';
import Partner from '@/domains/Partners/Partner';
import Store from '@/domains/Stores/Store';
import Unit from '@/domains/Units/Unit';
import Company from '@/domains/Companies/Company';
import Package from '@/domains/Packages/Package';
import Multiplier from "@/core/types/Multiplier";
import Qty from "@/core/types/Qty";
import Dimension from "@/core/types/Dimension";
import Volume from "@/core/types/Volume";
import Density from "@/core/types/Density";
import Weight from "@/core/types/Weight";
import VatRate from "@/core/types/VatRate";
import DemandV2 from "@/domains/DemandsV2/DemandV2";

abstract class AbstractOrderOption extends BaseEntity {
  /** Средняя плотность груза кг/м3. Density, а не число: сравнивается с
   *  packageCargoDensity, а Numeric операторы сравнения не поддерживает. */
  static AVERAGE_CARGO_DENSITY = Density.fromServer('344');

  /** наименование */
  public name: string | null;

  /** Производитель */
  @Title_ru_RU('производитель', TYPES.OBJECT)
  @serializeSimplified
  public manufacturer: Manufacturer | null;

  /** --------- seller ------------ */

  /** Продавец */
  @serializeSimplified
  protected _seller: Partner;

  /** Юр лицо продавца */
  @serializeSimplified
  protected _sellerCompany: Company | null;

  /** Склад поставщика */
  @serializeSimplified
  protected _sellerStore: Store | null;

  /** Цена поставщика */
  protected _priceSeller: Price;

  /** Цена поставщика с учетом НДС */
  public priceFullSeller: Price;

  /** Ставка НДС продавца */
  @Title_ru_RU('НДС поставщика', TYPES.NUMBER_PERCENT)
  protected _vatRateSeller: VatRate | null;

  /** НДС включен или нет */
  protected _isVatIncludedSeller: boolean | null;

  protected _sellersCode: string | null;

  /** --------- buyer ------------ */
  /** Когда поставщик создаёт офер под конкретного покупателя, то
   * есть удовлетворяет потребность, или через тендер, то было принято решение
   * сохранять данные этого покупателя в поля */
  @serializeSimplified
  public buyer: Partner | null;

  @serializeSimplified
  public buyerStore: Store;

  /** Конечная цена для покупателя */
  public priceFullBuyer: Price | null;

  /** ставка НДС покупателя */
  public vatRateBuyer: VatRate;

  /** Для нас включён */
  public isVatIncludedBuyer: boolean = true;

  /** --------- кол-во ------------ */

  @Title_ru_RU('доступное кол-во', TYPES.NUMBER)
  protected _qtyAvailable: Qty | null;

  public qtyOrdered: Qty;

  public qtyLeft: Qty | null;

  /** Минимальное кол-во по предзаказу */
  @Title_ru_RU('минимальное количество по предзаказу', TYPES.NUMBER)
  public preorderMinQty: Qty | null;

  /** Минимальный страховой запас для предзаказа */
  @Title_ru_RU('минимальный страховой запас', TYPES.NUMBER)
  public preorderMonthlyReserve: Qty | null;

  /** --------- currency ------------ */

  /** Валюта */
  protected _offerCurrencyCode: string;

  /** --------------------------- */

  /** Срок годности/партия годна до */
  @Title_ru_RU('партия годна до', TYPES.DATE)
  public productExpirationDate: DateSimple | null;

  /** --------- Оплата ------------ */

  /** Условия оплаты */
  protected _paymentTerms: PaymentTerm[] = [
    new PaymentTerm(
      PaymentTermsService.getEventById('after_receiving'),
      50,
    ),
  ];

  /** priceModProfit */
  public priceMod: Multiplier;

  /** Цена перевозки за ед. формируется с учетом коэффициента плотности сырья */
  @Title_ru_RU('цена доставки', TYPES.PRICE)
  public deliveryPricePerUnit: Price = Price.fromEmpty();

  /** Полная цена для покупателя с учётом отсрочки по оплате */
  public priceFullBuyerWithDeferredPayment: Price | null;

  /** Наценка за отсрочку платежа множителем (1 — отсрочки нет) */
  public priceModDeferredPayment: Multiplier = Multiplier.fromPercent(0);

  /** Планируемая дата оплаты поставщику */
  @Title_ru_RU('дата оплаты поставщику', TYPES.DATE)
  public plannedPaymentDateToSeller: DateSimple;

  /** Дата получения денег от покупателя */
  @Title_ru_RU('дата плановой оплаты СФТ', TYPES.DATE)
  public plannedPaymentDateByBuyer: DateSimple;

  /** --------- Доставка ------------ */

  /** интервал отгрузки */
  @Title_ru_RU('интервал отгрузки', TYPES.DATE_RANGE)
  protected _shipmentPeriod: DateRange = new DateRange();

  /**
   * Период получения = период отгрузки плюс дни доставки.
   * Пересчитывается сеттерами обоих слагаемых, руками не присваивается.
   */
  @Title_ru_RU('интервал получения', TYPES.DATE_RANGE)
  public receiptPeriod: DateRange = new DateRange();

  /** true - нужно забрать со склада поставщика; false - поставщик доставит сам */
    // legacy поле, на бэке отсутствует
  protected _isDeliveryNeeded: boolean | null;

  /** Тип доставки */
  protected _deliveryType: DeliveryType;

  /** Срок доставки до покупателя, в случае если мы доставляем, то 12 дней */
  protected _deliveryDays: number | null;

  /** --------- Другое ------------ */

  /** единица измерения */
  @serializeSimplified
  public unit: Unit | null;

  /** Упаковка */
  @Title_ru_RU('упаковка', TYPES.OBJECT)
  @serializeSimplified
  public package: Package | null;

  /** Фасовка */
  protected _packageVolume: Qty | null;

  /** Длинна упаковки */
  @Title_ru_RU('длина упаковки', TYPES.NUMBER)
  protected _packageLength: Dimension | null;

  /** Ширина упаковки */
  @Title_ru_RU('ширина упаковки', TYPES.NUMBER)
  protected _packageWidth: Dimension | null;

  /** Высота упаковки */
  @Title_ru_RU('высота упаковки', TYPES.NUMBER)
  protected _packageHeight: Dimension | null;

  /** Объем в м3 одной упаковки с товаром, либо заполняется поставщиком сразу, либо рассчитыватся ДхШхВ */
  @Title_ru_RU('объем упаковки', TYPES.NUMBER)
  protected _packageCargoVolume: Volume | null;

  /** Плотность сырья */
  @Title_ru_RU('плотность сырья', TYPES.NUMBER)
  protected _packageCargoDensity: Density | null;

  /** Вес упаковки, обязательно заполняется поставщиком */
  @Title_ru_RU('вес упаковки', TYPES.NUMBER)
  protected _packageWeight: Weight | null;

  /** Из маршрута можно будет получить стоимость перевозки за 1 кг/м3 */
  protected _transportationRoute: TransportRoute | null;

  /** Стоимость перевозки по маршруту за кг в валюте офера */
  @Title_ru_RU('стоимость превозки по маршруту за кг', TYPES.NUMBER)
  public routeTransportationCostPerKg: Price | null;

  /** Стоимость перевозки по маршруту за м3 в валюте офера */
  @Title_ru_RU('стоимость превозки по маршруту за м3', TYPES.NUMBER)
  public routeTransportationCostPerM3: Price | null;

  /** Температурный режим перевозки */
  public storeTemperatureRange: IntRange = new IntRange();

  /** особые условия перевозки */
  protected _storeRulesText: string | null;

  /** описание */
  protected _description: string | null;


  /** ------------- фронтовские поля, на бэке отсутствуют ------------- */

  /** Дни разницы между желаемым днём оплаты, и фактическим днём оплаты поставщику */
  public daysBetween: number | null;

  /** Цена кредита в день */
  public dailyBankRate: number = (21 + 3) / 365;

  /**
   * Дата, на которую берётся курс валюты для пересчёта цен.
   * Реализуется в потомках: TenderBid → tender.startAt, DemandOption → дата заявки и т.д.
   */
  abstract getDateForCurrencyRate(): DateSimple;

  static fromServer(dataFromServer: any, result: AbstractOrderOption): AbstractOrderOption {
    super.fromServer(dataFromServer, result);

    result.buyer = Core.utils.hydrate(dataFromServer.buyer, Partner.fromServer)
    result.buyerStore = Core.utils.hydrate(dataFromServer.buyerStore, Store.fromServer)

    result.manufacturer = Core.utils.hydrate(dataFromServer.manufacturer, Manufacturer.fromServer)

    result._seller = Core.utils.hydrate(dataFromServer.seller, Partner.fromServer);
    result._sellerCompany = Core.utils.hydrate(dataFromServer.sellerCompany, Company.fromServer);
    result._sellerStore = Core.utils.hydrate(dataFromServer.sellerStore, Store.fromServer)

    result.unit = Core.utils.hydrate(dataFromServer.unit, Unit.fromServer)
    result.package = Core.utils.hydrate(dataFromServer.package, Package.fromServer)
    result._deliveryType = Core.utils.hydrate(dataFromServer.deliveryType, DeliveryType.fromServer);

    /** Временное решение */

    result._isDeliveryNeeded = result.deliveryType.id !== 'bySeller';

    result._shipmentPeriod = Core.utils.hydrate(dataFromServer.shipmentPeriod, DateRange.fromServer);
    result.receiptPeriod = Core.utils.hydrate(dataFromServer.receiptPeriod, DateRange.fromServer);

    result.storeTemperatureRange = Core.utils.hydrate(dataFromServer.storeTemperatureRange, IntRange.fromServer) ?? new IntRange();
    result.productExpirationDate = Core.utils.hydrate(dataFromServer.productExpirationDate, DateSimple.fromServer);

    /** Numeric-типы через Serializable.fromServer не подхватываются —
     *  гидрируем явно. Валюту, дату курса и единицу цена больше не несёт:
     *  они приходят параметрами в месте отображения и расчёта. */
    result._priceSeller = Price.fromServer(dataFromServer.priceSeller);
    result.priceFullSeller = Price.fromServer(dataFromServer.priceFullSeller);

    /** При создании офера цены покупателя нет, почему-то */
    if (dataFromServer.priceFullBuyer) {
      result.priceFullBuyer = Price.fromServer(dataFromServer.priceFullBuyer);
    }

    result._vatRateSeller = Core.utils.hydrate(dataFromServer.vatRateSeller, VatRate.fromServer);
    result.vatRateBuyer = Core.utils.hydrate(dataFromServer.vatRateBuyer, VatRate.fromServer);

    result._qtyAvailable = Core.utils.hydrate(dataFromServer.qtyAvailable, Qty.fromServer);
    result.qtyOrdered = Core.utils.hydrate(dataFromServer.qtyOrdered, Qty.fromServer);
    result.qtyLeft = Core.utils.hydrate(dataFromServer.qtyLeft, Qty.fromServer);

    result.preorderMinQty = Core.utils.hydrate(dataFromServer.preorderMinQty, Qty.fromServer);
    result.preorderMonthlyReserve = Core.utils.hydrate(dataFromServer.preorderMonthlyReserve, Qty.fromServer);

    /** Наценки приходят ПРОЦЕНТАМИ — fromPercent, а не fromServer. */
    result.priceMod = Core.utils.hydrate(dataFromServer.priceMod, Multiplier.fromPercent);

    result._packageVolume = Core.utils.hydrate(dataFromServer.packageVolume, Qty.fromServer);
    result._packageLength = Core.utils.hydrate(dataFromServer.packageLength, Dimension.fromServer);
    result._packageWidth = Core.utils.hydrate(dataFromServer.packageWidth, Dimension.fromServer);
    result._packageHeight = Core.utils.hydrate(dataFromServer.packageHeight, Dimension.fromServer);
    result._packageCargoVolume = Core.utils.hydrate(dataFromServer.packageCargoVolume, Volume.fromServer);
    result._packageCargoDensity = Core.utils.hydrate(dataFromServer.packageCargoDensity, Density.fromServer);
    result._packageWeight = Core.utils.hydrate(dataFromServer.packageWeight, Weight.fromServer);

    result.routeTransportationCostPerKg = Core.utils.hydrate(dataFromServer.routeTransportationCostPerKg, Price.fromServer);
    result.routeTransportationCostPerM3 = Core.utils.hydrate(dataFromServer.routeTransportationCostPerM3, Price.fromServer);

    result._paymentTerms = Core.utils.hydrateArray(dataFromServer.paymentTerms, PaymentTerm.fromServer);

    if (dataFromServer.priceFullBuyerWithDeferredPayment) {
      result.priceFullBuyerWithDeferredPayment = Price.fromServer(dataFromServer.priceFullBuyerWithDeferredPayment);
    } else {
      // Если с сервера не пришло значение, его всё равно нужно инициализировать
      result.priceFullBuyerWithDeferredPayment = Price.fromEmpty();
    }

    result.plannedPaymentDateToSeller = Core.utils.hydrate(dataFromServer.plannedPaymentDateToSeller, DateSimple.fromServer)
    result.plannedPaymentDateByBuyer = Core.utils.hydrate(dataFromServer.plannedPaymentDateByBuyer, DateSimple.fromServer);

    if (result.plannedPaymentDateToSeller && result.plannedPaymentDateByBuyer) {
      result.daysBetween = Math.abs(result.plannedPaymentDateToSeller.diffInDays(result.plannedPaymentDateByBuyer));
    }

    if (dataFromServer.deliveryPricePerUnit) {
      result.deliveryPricePerUnit = Price.fromServer(dataFromServer.deliveryPricePerUnit);
    } else {
      // нужно для DemandOption, DemandOption можно создать без поля deliveryPricePerUnit, но после создания должна быть возможность его заполнить
      result.deliveryPricePerUnit = Price.fromEmpty();
    }

    result._transportationRoute = Core.utils.hydrate(dataFromServer.transportRoute, TransportRoute.fromServer);

    return result;
  }

  /**
   * Поля, приходящие из офера. Тендер здесь не участвует: всё, что зависит
   * от него, заполняется отдельно — см. TenderBid.setTender.
   */
  static fromOffer(offer: Offer, result: AbstractOrderOption): AbstractOrderOption {
    /** ПОРЯДОК ИНИЦИАЛИЗАЦИИ ПОЛЕЙ ИМЕЕТ ЗНАЧЕНИЕ  */
    result.name = offer.name;

    result.buyer = offer.buyer;
    result.buyerStore = offer.buyerStore;

    result._seller = offer.seller;
    result._sellerCompany = offer.sellerCompany;
    result._sellerStore = offer.sellerStore;

    /** Некоторые поля инициализируем из офера */
    result.manufacturer = offer.manufacturer;
    result._offerCurrencyCode = offer.offerCurrencyCode;

    result._vatRateSeller = offer.vatRateSeller;
    result._isVatIncludedSeller = offer.isVatIncludedSeller;

    result.unit = offer.unit;
    result.package = offer.package;
    result._packageVolume = offer.packageVolume;

    result._paymentTerms = offer.paymentTerms;
    result._deliveryType = offer.deliveryType;
    result._deliveryDays = offer.deliveryDays;

    if (offer.shipmentPeriod.isFilled()) {
      const shipmentPeriodStart = DateSimple.fromServer(offer.shipmentPeriod.start);
      const shipmentPeriodEnd = DateSimple.fromServer(offer.shipmentPeriod.end);

      const today = DateSimple.today();

      if (shipmentPeriodEnd.isBefore(today)) {
        // весь период в прошлом — берём как есть, пользователь скорректирует в диалоге
        result.shipmentPeriod = new DateRange(shipmentPeriodStart, shipmentPeriodEnd);
      } else {
        let effectiveStart: DateSimple;

        if (shipmentPeriodStart.isBefore(today)) {
          effectiveStart = today;
        } else {
          effectiveStart = shipmentPeriodStart;
        }

        result.shipmentPeriod = new DateRange(effectiveStart, shipmentPeriodEnd);
      }
    }

    result.productExpirationDate = offer.productExpirationDate;


    result._sellersCode = offer.sellersCode;

    result.storeTemperatureRange = offer.storeTemperatureRange;
    result.storeRulesText = offer.storeRulesText;

    result.description = offer.description;

    /** ===================== ВГХ ===================== */
    if (offer.packageHeight) {
      result._packageHeight = offer.packageHeight;
    }
    if (offer.packageLength) {
      result._packageLength = offer.packageLength;
    }
    if (offer.packageWidth) {
      result._packageWidth = offer.packageWidth;
    }
    if (offer.packageWeight) {
      result._packageWeight = offer.packageWeight;
    }
    if (offer.packageCargoDensity) {
      result._packageCargoDensity = offer.packageCargoDensity;
    }
    if (offer.packageCargoVolume) {
      result._packageCargoVolume = offer.packageCargoVolume;
    }


    result._priceSeller = offer.priceSeller;
    result.priceFullSeller = offer.priceFullSeller;
    result.priceFullBuyer = offer.priceFullBuyer;
    result.priceFullBuyerWithDeferredPayment = offer.priceFullBuyerWithDeferredPayment;

    result.deliveryPricePerUnit = offer.deliveryPricePerUnit;

    return result;
  }

  static fromDemandV2(demandV2: DemandV2, result: AbstractOrderOption): AbstractOrderOption {
    result.buyer = demandV2.buyer;
    result.buyerStore = demandV2.buyerStore;

    result._offerCurrencyCode = demandV2.currencyCode;
    result.unit = Core.utils.cloneDeep(demandV2.unit);

    result._priceSeller = Price.fromEmpty();
    result.priceFullSeller = Price.fromEmpty();
    result.priceFullBuyer = Price.fromEmpty();
    result.priceFullBuyerWithDeferredPayment = Price.fromEmpty();
    result.deliveryPricePerUnit = Price.fromEmpty();

    /** Заявку по потребности везёт покупатель, срок фиксированный. */
    result.deliveryType = Core.dictionaries.deliveryTypes.get('byBuyer');
    result.deliveryDays = 12;

    return result;
  }

  set seller(value: Partner) {
    /** reset уже не консистентных данных **/
    this.sellerCompany = null;
    this.sellerStore = null;

    this._seller = value;

    /** FIXME подстановка единственного юр лица и склада переехала сюда из
     *  assumeSellerCompany / assumeSellerStore и здесь не место: выбор
     *  значения по умолчанию — работа источника, а не модели. Заводите
     *  SelectLoader с default() на соответствующих контролах и удаляйте
     *  этот блок.
     *
     * if (Core.utils.isNotEmptyArray(this._seller?.companies) && this._seller.companies.length === 1) {
     *   CompaniesService.get(this._seller.companies[0].id)
     *     .then((company: Company) => {
     *       this.sellerCompany = company;
     *     });
     * }
     *
     * if (Core.utils.isNotEmptyArray(this._seller?.stores) && this._seller.stores.length === 1) {
     *   this.sellerStore = this._seller.stores[0];
     * }
     */
  }

  // геттер используется в домене оферов, очень удобно
  get isMine(): boolean {
    return this._seller?.id === Core.currentPartner!.id
  }

  get seller(): Partner {
    return this._seller;
  }

  set sellerCompany(value: Company | null) {
    this._sellerCompany = value;

    this.setValuesBasedOnSellerCompany();

    this.calculatePrices();
  }

  get sellerCompany(): Company | null {
    return this._sellerCompany;
  }

  set sellerStore(value: Store | null) {
    this._sellerStore = value;
  }

  get sellerStore(): Store | null {
    return this._sellerStore;
  }

  set transportationRoute(value: TransportRoute | null) {
    this._transportationRoute = value;

    if (this._transportationRoute) {
      this.calculatePrices();
    }
  }

  get transportationRoute(): TransportRoute | null {
    return this._transportationRoute;
  }

  set sellersCode(value: string | null) {
    this._sellersCode = value || null;
  }

  get sellersCode(): string | null {
    return this._sellersCode;
  }

  set isVatIncludedSeller(value: boolean | null) {
    this._isVatIncludedSeller = value;

    this.calculatePrices();
  }

  get isVatIncludedSeller(): boolean | null {
    return this._isVatIncludedSeller;
  }

  set vatRateSeller(value: VatRate | null) {
    this._vatRateSeller = value;

    this.calculatePrices();
  }

  get vatRateSeller(): VatRate | null {
    return this._vatRateSeller;
  }

  set qtyAvailable(value: Qty | null) {
    this._qtyAvailable = value;
  }

  get qtyAvailable(): Qty | null {
    return this._qtyAvailable;
  }

  set offerCurrencyCode(value: string) {
    this._offerCurrencyCode = value;

    /** Прежде здесь переставляли валюту у каждой цены — Price её не несёт,
     *  все цены офера выражены в offerCurrencyCode по построению. */

    this.calculatePrices();
  }

  get offerCurrencyCode(): string {
    return this._offerCurrencyCode;
  }

  /**
   * Условия оплаты целиком. Правило «до отгрузки — ровно 5 дней» живёт
   * в сеттере PaymentTerm.event, здесь не дублируется.
   */
  set paymentTerms(value: PaymentTerm[]) {
    this._paymentTerms = value;

    if (this.hasPaymentTerms()) {
      this.calculatePriceFullBuyerWithDeferredPayment();
    } else {
      this.resetDeferredPayment();
    }
  }

  get paymentTerms(): PaymentTerm[] {
    return this._paymentTerms;
  }

  // на данный момент мы всегда храним в массиве один айтем. интерфейс расчитан на один айтем
  set paymentTerm(value: PaymentTerm) {
    this.paymentTerms = [value]
  }

  get paymentTerm(): PaymentTerm {
    return this._paymentTerms[0]
  }

  /** f/6426 добавил сеттер и геттер чтобы не было ошибки isDeliveryNeeded */
  set isDeliveryNeeded(value: boolean | null) {
    this._isDeliveryNeeded = value;
  }

  get isDeliveryNeeded(): boolean | null {
    return this._isDeliveryNeeded;
  }

  @Title_ru_RU('тип доставки', TYPES.OBJECT)
  set deliveryType(value: DeliveryType) {
    this._deliveryType = value;

    /** Платформа везёт за 12 дней, поставщик — своим сроком, его вводят руками. */
    if (this._deliveryType.id === 'byETP') {
      this.deliveryDays = 12;
    } else {
      this.deliveryDays = null;
    }

    /** Зовём явно, хотя присваивание deliveryDays выше уже дёрнуло пересчёт:
     *  связь через другой сеттер неявная и ломается от любой его правки. */
    this.calculateShipmentPeriod();

    this.calculatePriceMod();

    //пересчитать цену, если тип доставки изменили,
    // так как стоимость доставки за ед. считается  влюбом случае,
    // но должна быть включена в конечную цену для покупателя толко при доставке платформой,
    // зачастую, делая ставку продавец, может в последний момент изменить тип доставки,
    // чтобы его предложение стало лучшим, либо решил, что ему выгоднее организовать доставку самостоятельно
    if (this.priceSeller) {
      this.calculatePrices();
    }
  }

  get deliveryType(): DeliveryType {
    return this._deliveryType;
  }

  @Title_ru_RU('срок доставки', TYPES.OBJECT)
  set deliveryDays(value: number | null) {
    this._deliveryDays = value;

    /** Порядок важен: период отгрузки правится первым, период получения
     *  считается уже от нового. */
    this.calculateShipmentPeriod();
    this.calculateReceiptPeriod();

    if (this.hasDeliveryDays() && this.hasPaymentTerms()) {
      this.calculatePriceFullBuyerWithDeferredPayment();
    } else {
      this.resetDeferredPayment();
    }
  }

  get deliveryDays(): number | null {
    return this._deliveryDays;
  }

  @Title_ru_RU('период отгрузки', TYPES.DATE_RANGE)
  /** Смена периода отгрузки двигает и период получения — он от неё считается. */
  set shipmentPeriod(value: DateRange) {
    this._shipmentPeriod = value;

    this.calculateReceiptPeriod();
  }

  get shipmentPeriod(): DateRange {
    return this._shipmentPeriod;
  }

  set storeRulesText(value: string | null) {
    this._storeRulesText = value || null;
  }

  get storeRulesText(): string | null {
    return this._storeRulesText;
  }

  set description(value: string | null) {
    this._description = value || null;
  }

  get description(): string | null {
    return this._description;
  }

  set priceSeller(value: Price) {
    this._priceSeller = value;
    this.calculatePrices();
  }

  get priceSeller(): Price {
    return this._priceSeller;
  }

  set packageCargoDensity(value: Density | null) {
    this._packageCargoDensity = value;
  }

  get packageCargoDensity(): Density | null {
    return this._packageCargoDensity;
  }

  set packageCargoVolume(value: Volume | null) {
    this._packageCargoVolume = value;

    this.calculatePrices();
  }

  get packageCargoVolume(): Volume | null {
    if (!this._packageCargoVolume) return null;
    return this._packageCargoVolume;
  }

  set packageWeight(value: Weight | null) {
    this._packageWeight = value;

    this.calculatePrices();
  }

  get packageWeight(): Weight | null {
    if (!this._packageWeight) return null;

    return this._packageWeight;
  }

  calculatePlannedPaymentDateToSeller(): void {
    /** Имплиментруйте в потомках */
  }

  calculatePlannedPaymentDateByBuyer(): void {
    /** Имплиментруйте в потомках */
  }

  async calculatePriceFullBuyerWithDeferredPayment(): Promise<void> {

    if (!this.priceSeller) return;

    this.calculatePlannedPaymentDateToSeller();
    this.calculatePlannedPaymentDateByBuyer();

    this.daysBetween = Math.abs(
      Core.utils.date.toDays(this.plannedPaymentDateToSeller.subtractDate(this.plannedPaymentDateByBuyer)),
    );

    /** Наценка это и есть цена кредита. dailyBankRate — суточная ставка в
     *  процентах, остаётся числом: при переводе в множитель посуточная доля
     *  процента не пережила бы округление. В Multiplier заворачиваем уже
     *  результат за весь период. */
    let percentDeferredPayment = Core.utils.number.round10(this.dailyBankRate * this.daysBetween, -2);

    /** Если поставщик даёт нам условия даже лучше, чем мы желаем, то расчёт
     * priceFullBuyerWithDeferredPayment будет с отрицательной наценкой */
    if (this.plannedPaymentDateToSeller.isAfter(this.plannedPaymentDateByBuyer)) {
      percentDeferredPayment *= -1;
    }

    if (percentDeferredPayment < 0) {
      this.priceModDeferredPayment = Multiplier.fromPercent(0);
    } else {
      this.priceModDeferredPayment = Multiplier.fromPercent(percentDeferredPayment);
    }

    this.priceFullBuyerWithDeferredPayment = Core.utils.money.getPriceFullBuyerInGivenCurrency(
      this.priceFullSeller,
      this.vatRateSeller,
      this.vatRateBuyer,
      this.priceMod.plus(this.priceModDeferredPayment),
      this.deliveryPricePerUnit,
      this.deliveryType,
    );
  }

  /** Выделено в отдельный метод, потому что изменение ндсов, и прочей ботвы вызывает пересчёт
   * цен */
  async calculatePrices(): Promise<void> {

    /**
     * Мы можем рассчитать цену, только если есть priceSeller и offerCurrencyCode, и ндс.
     *
     * Условия НДС считаем известными в двух случаях: юрлицо выбрано (тогда
     * isVatPayer отвечает и за неплательщика, у которого ставки нет), либо
     * ставка проставлена сама — у нового поставщика юрлица ещё нет, а ставка
     * уже взята из локальной юрисдикции.
     */
    if (
      this.priceSeller
      && !this.priceSeller?.isEmpty()
      && this.offerCurrencyCode
      && (typeof this.sellerCompany?.isVatPayer === 'boolean' || this.vatRateSeller !== null)
    ) {
      /**
       * Расчет priceFullBuyer зависит от предварительного вычисления deliveryPricePerUnit.
       */
      await this.calculateDeliveryPricePerUnit();

      this.calculatePriceMod();
      this.calculateFullPriceSeller();

      this.calculatePriceFullBuyer();
      await this.calculatePriceFullBuyerWithDeferredPayment();
    }
  }

  calculateFullPriceSeller(): void {
    /** Юрлицо отвечает на вопрос о плательщике точнее всего, но у нового
     *  поставщика его ещё нет — тогда судим по наличию ставки. */
    const isVatPayer = this.sellerCompany?.isVatPayer ?? (this.vatRateSeller !== null);

    this.priceFullSeller = Core.utils.money.getPriceFullSellerInGivenCurrency(
      this.priceSeller,
      this.isVatIncludedSeller!,
      isVatPayer,
      this.vatRateSeller!,
    );
  }

  calculatePriceFullBuyer(): void {
    const priceFullBuyer = Core.utils.money.getPriceFullBuyerInGivenCurrency(
      this.priceFullSeller,
      this.vatRateSeller,
      this.vatRateBuyer,
      this.priceMod,
      this.deliveryPricePerUnit,
      this.deliveryType,
    );

    Core.addBreadcrumb('calculatePriceFullBuyer: расчёт', {
      input: {
        priceSeller: this.priceSeller,
        priceFullSeller: this.priceFullSeller,
        vatRateSeller: this.vatRateSeller,
        vatRateBuyer: this.vatRateBuyer,
        isVatIncludedSeller: this.isVatIncludedSeller,
        priceMod: this.priceMod,
        deliveryPricePerUnit: this.deliveryPricePerUnit,
        deliveryType: this.deliveryType?.id,
      },
      result: priceFullBuyer,
    });

    this.priceFullBuyer = priceFullBuyer;
  }

  setValuesBasedOnSellerCompany(): void {
  }

  /** В потомках реализовано **/
  calculatePriceMod(): void {
  }

  resetDeferredPayment(): void {
    this.priceFullBuyerWithDeferredPayment = Price.fromEmpty();
    this.priceModDeferredPayment = Multiplier.fromPercent(0);
  }

  hasDeliveryDays(): boolean {
    return Boolean(this._deliveryDays && this._deliveryDays > 0);
  }

  /**
   * Условия оплаты заданы: список непустой и каждая часть заполнена. Расчёт
   * отсрочки идёт по всем частям платежа, недозаполненная часть ломает его
   * так же, как отсутствие всего списка.
   */
  hasPaymentTerms(): boolean {
    if (Core.utils.isNotEmptyArray(this._paymentTerms) === false) {
      return false;
    } else {
      return this._paymentTerms.every(
        paymentTerm => paymentTerm.event !== null && paymentTerm.days !== null,
      );
    }
  }

  set packageVolume(value: Qty | null) {
    this._packageVolume = value;

    this.onPackageVolumeChange();
  }

  get packageVolume(): Qty | null {
    return this._packageVolume;
  }

  onPackageVolumeChange(): void {
    this.calculatePrices();
  }

  set packageLength(value: Dimension | null) {
    this._packageLength = value;
    this.calculatePackageCargoVolume();
  }

  get packageLength(): Dimension | null {
    return this._packageLength;
  }

  set packageWidth(value: Dimension | null) {
    this._packageWidth = value;
    this.calculatePackageCargoVolume();
  }

  get packageWidth(): Dimension | null {
    return this._packageWidth;
  }

  set packageHeight(value: Dimension | null) {
    this._packageHeight = value;
    this.calculatePackageCargoVolume();
  }

  get packageHeight(): Dimension | null {
    return this._packageHeight;
  }

  /** Период отгрузки, которого требует покупатель. Переписан в ставке */
  getDesiredShipmentPeriod(): DateRange | null {
    return null;
  }

  /** Подгоняет период отгрузки под требование покупателя. */
  calculateShipmentPeriod(): void {
    const desiredShipmentPeriod = this.getDesiredShipmentPeriod();

    if (Core.utils.isNullOrUndefined(desiredShipmentPeriod) === true) {
      // требования нет — период отгрузки остаётся как есть
    }
    else if (this._shipmentPeriod.isFilled() && this._shipmentPeriod.contains(desiredShipmentPeriod)) {
      // период отгрузки покрывает требуемый — менять нечего
    }
    else {
      // период уже требуемого (или пуст) — отгружать придётся по требованию покупателя
      this.shipmentPeriod = desiredShipmentPeriod!;

      Core.showNotification.info(
        `Период отгрузки изменён на ${this.shipmentPeriod} — так его требует покупатель`,
      );
    }
  }

  /**
   * Период получения = период отгрузки плюс дни доставки.
   *
   * Зовётся сеттерами обоих слагаемых. Прежде расчёт жил в Offer, но
   * пересчёт нужен и ставке: её период отгрузки правится в диалоге.
   */
  calculateReceiptPeriod(): void {
    if (this._shipmentPeriod.isFilled() && this._deliveryDays) {
      this.receiptPeriod = this._shipmentPeriod.addDays(this._deliveryDays);
    }
  }

  /**
   * Рассчитывает объем одной упаковки с товаром в м3
   */
  calculatePackageCargoVolume(): void {
    /**
     * Dimension × Dimension → Area (мм²), Area × Dimension → Volume (м³):
     * перевод миллиметров в кубометры вшит в Area.mul, деления на 1000
     * здесь больше нет.
     *
     * Ручное округление до 3 знаков и краевое «не меньше 0.001» тоже ушли:
     * у Volume scale 3 и ROUND_UP, минимум 0.001 для любого ненулевого
     * груза гарантирован самим типом.
     */
    if (this.packageLength && this.packageWidth && this.packageHeight) {
      this._packageCargoVolume = this.packageLength
        .mul(this.packageWidth)
        .mul(this.packageHeight);

      /** Когда мы меняем параметр влияющий на цену, нужно пересчитать цены **/
      this.calculatePrices();
    }
  }

  /**
   * Рассчитывает стоимость провоза за условную единицу.
   *
   * Если плотность груза < AVERAGE_CARGO_DENSITY:
   *   (Объем упаковки с товаром / фасовка) × Цена доставки за м³
   * Если плотность груза >= AVERAGE_CARGO_DENSITY:
   *   (Вес упаковки с товаром / фасовка) × Цена доставки за 1 кг
   */
  async calculateDeliveryPricePerUnit(): Promise<void> {
    if (!this.transportationRoute || !this.packageWeight || !this.packageCargoVolume) {
      Core.addBreadcrumb('calculateDeliveryPricePerUnit: пропущен расчёт', {
        hasTransportationRoute: !!this.transportationRoute,
        packageWeight: this.packageWeight,
        packageCargoVolume: this.packageCargoVolume,
      });
      return;
    }

    this.calculatePackageCargoDensity();

    /** isNumber больше не годится: величины — объекты. «Не задано» у Numeric
     *  выражается isEmpty, отдельного null-поля нет. */
    if (
      Core.utils.isNullOrUndefined(this._packageCargoVolume) || this._packageCargoVolume.isEmpty()
      || Core.utils.isNullOrUndefined(this.packageVolume) || this.packageVolume.isEmpty()
      || Core.utils.isNullOrUndefined(this.packageCargoDensity) || this.packageCargoDensity.isEmpty()
    ) {
      Core.addBreadcrumb('calculateDeliveryPricePerUnit: невалидные входные данные', {
        packageCargoVolume: this._packageCargoVolume,
        packageVolume: this.packageVolume,
        packageCargoDensity: this.packageCargoDensity,
      });
      return;
    }

    let deliveryPricePerUnit: Price;
    let routeCost: Price;
    let priceMode;

    const dateForCurrencyRate = this.getDateForCurrencyRate();

    /**
     * Тариф маршрута выражен в costCurrencyCode, офер — в offerCurrencyCode,
     * поэтому конвертация настоящая, а не приведение валюты к самой себе.
     * Берём асинхронный MoneyUtils.convert: метод и так async, а расчёт —
     * не отображение, пустой результат из-за непрогретого кеша здесь
     * недопустим.
     *
     * Само умножение считаем в Decimal через value: операция «цена за м3 ×
     * объём на единицу» сокращает размерность, и подходящего метода в
     * Volume/Weight/Price нет. Заводить его ради одного места
     * преждевременно — появится такой расчёт ещё где-то, тогда и переносить.
     *
     * round10 до 3 знаков не нужен: scale цены и так 3, округление вшито
     * в конструктор через fromOperation.
     */
    if (this.packageCargoDensity.lessThan(AbstractOrderOption.AVERAGE_CARGO_DENSITY)) {
      priceMode = 'perM3';
      routeCost = await Core.utils.money.convert(
        this.transportationRoute.costPerM3,
        this.transportationRoute.costCurrencyCode,
        this.offerCurrencyCode,
        dateForCurrencyRate,
      );

      deliveryPricePerUnit = Price.fromOperation(
        this._packageCargoVolume.value
          .dividedBy(this.packageVolume.value)
          .times(routeCost.value),
      );
    } else {
      priceMode = 'perKg';
      routeCost = await Core.utils.money.convert(
        this.transportationRoute.costPerKg,
        this.transportationRoute.costCurrencyCode,
        this.offerCurrencyCode,
        dateForCurrencyRate,
      );

      deliveryPricePerUnit = Price.fromOperation(
        this.packageWeight.value
          .dividedBy(this.packageVolume.value)
          .times(routeCost.value),
      );
    }

    Core.addBreadcrumb('calculateDeliveryPricePerUnit: расчёт выполнен', {
      priceMode,
      package: {
        cargoDensity: this.packageCargoDensity,
        cargoVolume: this.packageCargoVolume,
        volume: this.packageVolume,
        weight: this.packageWeight,
      },
      routeCost: {
        value: routeCost,
        currency: this.offerCurrencyCode,
        routeCurrency: this.transportationRoute.costCurrencyCode,
      },
      result: deliveryPricePerUnit,
    });

    /** Прежде здесь была цепочка setValue / setDefaultCurrencyCode / setUnit —
     *  артефакт PriceOld, который носил в себе валюту и единицу измерения.
     *  Price — просто значение, присваиваем напрямую. */
    this.deliveryPricePerUnit = deliveryPricePerUnit;
  }

  /** Расчет плотности сырья */
  calculatePackageCargoDensity(): void {
    /** Weight / Volume → Density: округление до 2 знаков вшито в тип,
     *  ceilPrecise больше не нужен. */
    if (this.packageWeight && this.packageCargoVolume) {
      this.packageCargoDensity = this.packageWeight.div(this.packageCargoVolume);
    }
  }

  /** ================ Сравнение с требованием ================ */

  /**
   * Прокси к правилам Mismatch: source берётся из полей этой сущности,
   * требование приходит параметром. Гейт «есть ли чем сравнивать» стоит
   * здесь — правила пустых значений не принимают.
   */

  getReceiptDateMismatch(target: DateSimple): string | null {
    if (this.receiptPeriod.isEmpty()) {
      return null;
    }
    return Mismatch.getReceiptDateMismatch(this.receiptPeriod, target);
  }

  getQtyMismatch(target: Qty): string | null {
    if (Core.utils.isNullOrUndefined(this.qtyAvailable)) {
      return null;
    } else {
      return Mismatch.getQtyMismatch(this.qtyAvailable!, target, this.unit!);
    }
  }

  getPreorderShipmentPeriodMismatch(target: DateRange): string | null {
    return Mismatch.getPreorderShipmentPeriodMismatch(this.shipmentPeriod, target);
  }

  getExpirationDateMismatch(target: DateSimple): string | null {
    if (Core.utils.isNullOrUndefined(this.productExpirationDate)) {
      return null;
    } else {
      return Mismatch.getExpirationDateMismatch(this.productExpirationDate!, target);
    }
  }

  getManufacturerMismatch(target: Manufacturer[]): string | null {
    if (Core.utils.isNullOrUndefined(this.manufacturer)) {
      return null;
    } else if (Core.utils.isEmptyArray(target)) {
      return null;
    } else {
      return Mismatch.getManufacturerMismatchByList(this.manufacturer!, target);
    }
  }

  getSellerCompanyMismatch(target: Company[]): string | null {
    if (Core.utils.isNullOrUndefined(this.sellerCompany)) {
      return null;
    } else if (Core.utils.isEmptyArray(target)) {
      return null;
    } else {
      return Mismatch.getSellerCompanyMismatchByList(this.sellerCompany!, target);
    }
  }

  /**
   * Связка поставщик — юр. лицо — производитель не входит в разрешения на товар.
   * Разрешений на товар нет вовсе — участвовать могут все, расхождения нет.
   * Иначе сверяемся с действующими разрешениями этого поставщика.
   */
  getBundleMismatch(product: Product): string | null {
    if (product.isSupplyOfferRequired === false) {
      return null;
    } else if (Core.utils.isNullOrUndefined(this.sellerCompany)) {
      return null;
    } else if (Core.utils.isNullOrUndefined(this.manufacturer)) {
      return null;
    } else {
      const supplyOffersByPartner = product.getApprovedAndNotExcludedSupplyOffersByPartner(this.seller);

      return Mismatch.getBundleMismatch({
        seller: this.seller,
        sellerCompany: this.sellerCompany!,
        manufacturer: this.manufacturer!,
      }, supplyOffersByPartner);
    }
  }

  getUnitMismatch(target: Unit): string | null {
    if (Core.utils.isNullOrUndefined(this.unit)) {
      return null;
    } else {
      return Mismatch.getUnitMismatch(this.unit!, target);
    }
  }

  getPreorderReserveMismatch(target: Qty): string | null {
    if (Core.utils.isNullOrUndefined(this.preorderMonthlyReserve)) {
      return null;
    } else {
      return Mismatch.getPreorderReserveMismatch(this.preorderMonthlyReserve!, target, this.unit!);
    }
  }

  /**
   * Расхождение по цене с отсрочкой: наша цена выше требуемой.
   *
   * Обе стороны идут парой «цена + код валюты»: своя цена выражена в
   * offerCurrencyCode, требование — в targetCurrencyCode. Приведение к общей
   * валюте и дату курса берёт на себя правило, отсюда уходит getDateForCurrencyRate.
   */
  getPriceWithDeferredPaymentMismatch(targetPrice: Price, targetCurrencyCode: string): string | null {
    if (
      Core.utils.isNullOrUndefined(this.priceFullBuyerWithDeferredPayment)
      || this.priceFullBuyerWithDeferredPayment!.isEmpty()
    ) {
      return null;
    } else if (Core.utils.isNullOrUndefined(targetPrice) || targetPrice.isEmpty()) {
      return null;
    } else {
      return Mismatch.getPriceMismatch(
        this.priceFullBuyerWithDeferredPayment!,
        this.offerCurrencyCode,
        targetPrice,
        targetCurrencyCode,
        this.getDateForCurrencyRate(),
      );
    }
  }

  /**
   * Значения характеристик, которые сверяются с рамками товара.
   * У предложения свои, у ставки — в офере, поэтому реализуется в потомках.
   */
  getCharacteristics(): OfferCharacteristic[] {
    return [];
  }

  /**
   * Все расхождения с требованием — по элементу на каждое. Пустой список
   * означает, что предложение требованию отвечает целиком.
   *
   * Правила Mismatch здесь работают предикатом «расхождение есть»; сами
   * значения берутся из полей напрямую, чтобы показать обе стороны.
   *
   * Нужен там, где важен сам факт расхождения, а не конкретное поле:
   * точка в футере формы, отметка в реестре. Поштучно те же правила
   * зовутся напрямую — здесь просто всё сразу.
   */
  getMismatches(target: Demand | Tender): MismatchItem[] {
    if(!target) return;

    const result: MismatchItem[] = [];
    const product = target.product;
    const desiredManufacturers = product.getDesiredManufacturersByPartner(this.seller);


    const manufacturerMismatch = this.getManufacturerMismatch(desiredManufacturers);
    if (manufacturerMismatch !== null) {
      result.push({
        label:       'Производитель',
        valueSource: this.manufacturer?.name ?? '--',
        valueTarget: desiredManufacturers.map(_manufacturer => _manufacturer.name).join(', '),
      });
    }

    const unitMismatch = this.getUnitMismatch(target.unit);
    if (unitMismatch !== null) {
      result.push({
        label:       'Единица измерения',
        valueSource: this.unit?.toString() ?? '--',
        valueTarget: target.unit?.toString() ?? '--',
      });
    }

    const qtyMismatch = this.getQtyMismatch(target.qtyDesired);
    if (qtyMismatch !== null) {
      result.push({
        label:       'Количество',
        valueSource: this.qtyAvailable?.format(this.unit) ?? '--',
        valueTarget: target.qtyDesired?.format(target.unit) ?? '--',
      });
    }

    const bundleMismatch = this.getBundleMismatch(product);
    if (bundleMismatch !== null) {
      result.push({
        label:       'Разрешение на участие',
        valueSource: `${this.seller?.name} - ${this.sellerCompany?.name} - ${this.manufacturer?.name}`,
        valueTarget: bundleMismatch,
      });
    }

    if (product.hasExpirationRequirement) {
      const expirationDateMismatch = this.getExpirationDateMismatch(product.requiredExpirationDate);
      if (expirationDateMismatch !== null) {
        result.push({
          label:       'Срок годности',
          valueSource: Core.utils.date.format(this.productExpirationDate) ?? '--',
          valueTarget: Core.utils.date.format(product.requiredExpirationDate) ?? '--',
        });
      }
    }

    if (target.isPreorderNeeded) {
      const preorderShipmentPeriodMismatch = this.getPreorderShipmentPeriodMismatch(target.preorderShipmentPeriod!);
      if (preorderShipmentPeriodMismatch !== null) {
        result.push({
          label:       'Период отгрузки',
          valueSource: this.shipmentPeriod.toString(),
          valueTarget: target.preorderShipmentPeriod!.toString(),
        });
      }

      const preorderReserveMismatch = this.getPreorderReserveMismatch(target.preorderMonthlyReserve);
      if (preorderReserveMismatch !== null) {
        result.push({
          label:       'Страховой запас',
          valueSource: this.preorderMonthlyReserve?.format(this.unit) ?? '--',
          valueTarget: target.preorderMonthlyReserve?.format(target.unit) ?? '--',
        });
      }
    }
    else if (target.desiredReceiptDate) {
      const receiptDateMismatch = this.getReceiptDateMismatch(target.desiredReceiptDate);
      if (receiptDateMismatch !== null) {
        result.push({
          label:       'Желаемая дата получения',
          valueSource: this.receiptPeriod.toString(),
          valueTarget: Core.utils.date.format(target.desiredReceiptDate) ?? '--',
        });
      }
    }

    for (const _characteristic of this.getCharacteristics()) {
      const mismatch = _characteristic.getMismatchByProduct(product);

      // null - ситуация при которой характеристика есть в предложении, но её нет в продукте.
      // мы не смогли найти характеристику в продукте. То есть сравнивать характеристику офера не с чем
      if (mismatch !== null) {
        result.push({
          label:       _characteristic.name,
          valueSource: _characteristic.valueStringRepresentation,
          valueTarget: mismatch,
        });
      }
    }

    return result;
  }

  /** ================ Валидация ================ */

  validateExpirationDate(value: Date | null): string | null {
    if (!value) return null;

    const today = Core.utils.date.toDayStart(new Date());

    /** на случай редактирования, чтобы не блокировался весь календарь */

    if (value > today) {
      return null;
    }

    if (value <= today) {
      return 'Нельзя указать прошедшую дату';
    }

    if (
      (value instanceof Date) === false
      || this.shipmentPeriod.end === null
      || this.deliveryDays === null
    ) {
      return null;
    }

    /** addDays сам клонирует и возвращает новый DateSimple — исходная дата
     *  окончания отгрузки не мутируется. */
    const expectedDate = this.shipmentPeriod.end.addDays(this.deliveryDays + 5);

    if (value >= expectedDate) {
      return 'Окончание срока годности должно быть больше чем дата доставки на склад покупателя плюс 5 дней';
    }

    return null;
  }

  /**
   * Есть ли маршрут перевозки между зонами складов.
   */
  validateTransportationRoute(): string | null {
    if (Core.utils.isNullOrUndefined(this.transportationRoute)) {
      return `Нет маршрута перевозки со склада отправителя на склад получателя — обратитесь к администратору`;
    } else {
      return null;
    }
  }

  validate(product: Product | null = null): string[] {
    const errors: (string | null)[] = [
      this.sellerStore?.validateTransportZone(),
      this.buyerStore.validateTransportZone(),
      this.validateTransportationRoute(),
    ];

    /** Юр. лицо и производитель форма требует, к сабмиту оба заполнены.
     *  Гейт — на случай вызова из другого места: без них связки нет. */
    if (
      product !== null
      && Core.utils.isNullOrUndefined(this.sellerCompany) === false
      && Core.utils.isNullOrUndefined(this.manufacturer) === false
    ) {
      errors.push(
        product.validateSupplyOffer(this.seller, this.sellerCompany!, this.manufacturer!),
      );
    }

    return errors.filter((error: string | null) => error !== null) as string[];
  }
}

export default AbstractOrderOption;